/**
 * <ModuleCornerMarkers> — 8 red spheres at the GLB's CAD-frame AABB corners.
 *
 * Calibration helper: when two adjacent modules are placed correctly in the
 * chain (C0/C1 continuity), the corner sphere on Module N's exit face
 * overlaps the matching corner sphere on Module N+1's entry face. A
 * visible gap or overlap signals a bad origin-correction (or a bad chain
 * transform — but the latter is unit-tested).
 *
 * Lives inside the same group as the GLB <primitive>, so the markers move
 * along with the GLB through every transform (origin-correction, chain
 * start-world, layout-mode, orientation).
 *
 * `depthTest=false` keeps them visible even when sitting inside the stator
 * mesh — important for the U-turn module where the markers would otherwise
 * be hidden by the curved cover.
 */

import React, { useMemo } from 'react';
import type { Vec3 } from '../../types.js';

interface Props {
  /** GLB-frame AABB. Pass the bounds BEFORE origin correction. */
  bounds: { min: Vec3; max: Vec3 };
  sizeMm?: number;
  color?: string;
}

export const ModuleCornerMarkers: React.FC<Props> = ({
  bounds,
  sizeMm = 10,
  color = '#FF2030',
}) => {
  const corners = useMemo(() => cornersOfAabb(bounds), [bounds]);
  const radius = sizeMm / 2;

  return (
    <group name="module-corner-markers">
      {corners.map((c, i) => (
        <mesh key={i} position={c} renderOrder={9999}>
          <sphereGeometry args={[radius, 12, 12]} />
          <meshBasicMaterial
            color={color}
            toneMapped={false}
            depthTest={false}
            transparent
            opacity={0.95}
          />
        </mesh>
      ))}
    </group>
  );
};

function cornersOfAabb(b: { min: Vec3; max: Vec3 }): Vec3[] {
  const [minX, minY, minZ] = b.min;
  const [maxX, maxY, maxZ] = b.max;
  return [
    [minX, minY, minZ],
    [maxX, minY, minZ],
    [minX, maxY, minZ],
    [maxX, maxY, minZ],
    [minX, minY, maxZ],
    [maxX, minY, maxZ],
    [minX, maxY, maxZ],
    [maxX, maxY, maxZ],
  ];
}
